import { Appointment, DynamicSlotVariation, TimeSlotVariation } from '../types';
import { AVG_SPEED_MPH, MIN_GAP_MINUTES, getEventTypeColorDynamic } from './constants';

/**
 * Calculate drive time from distance using fixed average speed
 * FALLBACK ONLY - Prefer using actual duration from:
 * 1. Backend TravelInfo.travelTimeMinutes (from Google Maps Distance Matrix API)
 * 2. Frontend googleMapsDistanceService.calculateDrivingDistance()
 * 
 * This fixed-speed calculation is only used when:
 * - Google Maps API is not available
 * - Backend hasn't provided travel info
 * - As initial estimate before async Google Maps calculation completes
 * 
 * @param distance - Distance in miles
 * @returns Estimated drive time in minutes
 */
export const calculateDriveTime = (distance: number): number => {
  const timeInHours = Math.abs(distance) / AVG_SPEED_MPH;
  return Math.max(Math.round(timeInHours * 60), 1);
};

export const formatDriveTime = (minutes: number): string => {
  const positiveMinutes = Math.max(Math.round(minutes), 1);
  if (positiveMinutes < 60) return `${positiveMinutes} min`;
  const hours = Math.floor(positiveMinutes / 60);
  const remainingMinutes = positiveMinutes % 60;
  return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
};

/**
 * The appointment that holds the clock the latest out of `appointments`.
 *
 * Timeline cards are ordered by start time, which is not the same as the
 * order they free the user up: a synced calendar event running 10:00 AM –
 * 7:00 PM sorts ahead of a 10:00 – 10:30 meeting, so measuring the next
 * opening from its neighbouring card would advertise the whole afternoon as
 * free while the long event is still busy. Anchoring on the latest-ending
 * event instead coalesces overlapping busy time, matching how
 * `mergeBusyBlocks` finds bookable slots in `stopGapFinder`.
 */
export const findBusyAnchor = (appointments: Appointment[]): Appointment | null =>
  appointments.reduce<Appointment | null>(
    (latest, apt) =>
      !latest || apt.endDateTime.getTime() > latest.endDateTime.getTime() ? apt : latest,
    null
  );

export const hasTimeGap = (current: Appointment, next: Appointment): boolean => {
  const gapMinutes = Math.max(
    (next.startDateTime.getTime() - current.endDateTime.getTime()) / (1000 * 60),
    0
  );
  return gapMinutes >= MIN_GAP_MINUTES;
};

export const formatTimeGap = (current: Appointment, next: Appointment): string => {
  const gapMinutes = Math.max(
    (next.startDateTime.getTime() - current.endDateTime.getTime()) / (1000 * 60),
    0
  );
  const hours = Math.floor(gapMinutes / 60);
  const minutes = gapMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''} gap`;
  }
  return `${minutes}m gap`;
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'scheduled': return 'bg-blue-500';
    case 'complete': return 'bg-green-500';
    case 'no-show': return 'bg-ink-500';
    case 'cancelled': return 'bg-red-500';
    default: return 'bg-ink-500';
  }
};

export const getEventTypeColor = (type: string): string => {
  return getEventTypeColorDynamic(type);
};

export interface SlotBreakdown {
  appointmentTime: number;
  bufferTime: number;
  driveTime: number;
  totalDuration: number;
}

export interface EnhancedSlot {
  startTime: Date;
  endTime: Date;
  duration: number;
  appointmentDuration: number; // 45, 60, or 90 minutes
  breakdown: SlotBreakdown;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  isValid: boolean;
  slotType: 'standard' | 'extended' | 'compact';
}

// Standard appointment durations offered
export const APPOINTMENT_DURATIONS = {
  COMPACT: 45,    // Quick visits
  STANDARD: 60,   // Standard appointments  
  EXTENDED: 90    // Complex appointments
};

export const calculateAvailableSlot = (
  prevAppointment: Appointment, 
  nextAppointment: Appointment,
  _assumedDriveTime: number = 15 // Kept for API compatibility but not used in calculation
): { 
  startTime: Date; 
  endTime: Date; 
  duration: number; 
  breakdown: SlotBreakdown;
  isValid: boolean;
} => {
  // IMPORTANT: endDateTime already includes buffer time (e.g., 9:00-10:00 includes 45min service + 15min buffer)
  // Driver leaves at endDateTime, must arrive at next startDateTime
  // So we use raw times - buffer is already accounted for in endDateTime
  
  // Default appointment durations if not specified
  const MIN_SLOT_DURATION = 15; // Minimum slot to show (allows for short gaps)
  
  // Gap starts when driver leaves (buffer already included in endDateTime)
  const gapStartTime = new Date(prevAppointment.endDateTime.getTime());
  
  // Gap ends when driver must arrive at next appointment
  const gapEndTime = new Date(nextAppointment.startDateTime.getTime());
  
  // Calculate total available duration in minutes (the raw gap)
  const totalGapMinutes = Math.max(
    (gapEndTime.getTime() - gapStartTime.getTime()) / (1000 * 60),
    0
  );
  
  // Slot is valid if there's any meaningful gap (15+ minutes)
  // The actual appointment type validation happens separately in TimeSlotModal
  const isValid = totalGapMinutes >= MIN_SLOT_DURATION;
  
  // Calculate breakdown for display
  // Drive time estimate: 5 min each direction minimum
  const estimatedDriveTimeTotal = Math.min(10, Math.floor(totalGapMinutes * 0.2)); // ~20% for drive, min 10
  const appointmentTime = Math.max(totalGapMinutes - estimatedDriveTimeTotal, 0);
  
  const breakdown: SlotBreakdown = {
    appointmentTime,
    bufferTime: prevAppointment.postBufferMinutes || 0, // Buffer already in endDateTime
    driveTime: estimatedDriveTimeTotal,
    totalDuration: totalGapMinutes
  };
  
  return {
    startTime: gapStartTime,
    endTime: gapEndTime,
    duration: totalGapMinutes,
    breakdown,
    isValid
  };
};

/**
 * Calculate multiple slot options with different durations and priorities
 */
export const calculateEnhancedSlots = (
  prevAppointment: Appointment,
  nextAppointment: Appointment,
  assumedDriveTime: number = 15,
  customerLocation?: { lat: number; lng: number }
): EnhancedSlot[] => {
  const slots: EnhancedSlot[] = [];
  
  // Use dynamic buffer from appointment settings or defaults
  const postBuffer = prevAppointment.postBufferMinutes || 15;
  const preBuffer = nextAppointment.preBufferMinutes || 0;
  
  // Calculate gap boundaries
  const gapStartTime = new Date(prevAppointment.endDateTime.getTime() + (postBuffer * 60 * 1000));
  const gapEndTime = new Date(nextAppointment.startDateTime.getTime() - (preBuffer * 60 * 1000) - (assumedDriveTime * 60 * 1000));
  
  // Total available time
  const totalGapMinutes = Math.max(
    (gapEndTime.getTime() - gapStartTime.getTime()) / (1000 * 60),
    0
  );
  
  // Try different appointment durations
  Object.entries(APPOINTMENT_DURATIONS).forEach(([type, duration]) => {
    if (totalGapMinutes >= duration + assumedDriveTime) {
      const slotEndTime = new Date(gapStartTime.getTime() + (duration * 60 * 1000));
      
      // Calculate priority based on fit and location
      let priority: 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM';
      
      // High priority if it's a perfect fit (uses most of available time)
      const utilization = (duration + assumedDriveTime) / totalGapMinutes;
      if (utilization >= 0.8) {
        priority = 'HIGH';
      } else if (utilization <= 0.5) {
        priority = 'LOW';
      }
      
      // Adjust priority based on location clustering (if location provided)
      if (customerLocation && prevAppointment.latitude && prevAppointment.longitude) {
        const distance = calculateHaversineDistance(
          customerLocation,
          { lat: prevAppointment.latitude, lng: prevAppointment.longitude }
        );
        
        // Boost priority for nearby locations
        if (distance < 5 && priority === 'MEDIUM') {
          priority = 'HIGH';
        } else if (distance > 15 && priority === 'MEDIUM') {
          priority = 'LOW';
        }
      }
      
      const breakdown: SlotBreakdown = {
        appointmentTime: duration,
        bufferTime: postBuffer,
        driveTime: assumedDriveTime,
        totalDuration: totalGapMinutes
      };
      
      slots.push({
        startTime: gapStartTime,
        endTime: slotEndTime,
        duration: totalGapMinutes,
        appointmentDuration: duration,
        breakdown,
        priority,
        isValid: true,
        slotType: type.toLowerCase() as 'standard' | 'extended' | 'compact'
      });
    }
  });
  
  // Sort by priority and then by duration (prefer standard duration)
  return slots.sort((a, b) => {
    const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    
    // If same priority, prefer standard duration
    const durationPreference = { standard: 0, compact: 1, extended: 2 };
    return durationPreference[a.slotType] - durationPreference[b.slotType];
  });
};

/**
 * Calculate haversine distance between two points in miles
 */
export const calculateHaversineDistance = (
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (point2.lat - point1.lat) * Math.PI / 180;
  const dLng = (point2.lng - point1.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// All supported appointment durations in minutes
export const DYNAMIC_APPOINTMENT_DURATIONS = [15, 30, 45, 60, 90, 120, 180];

/**
 * Calculate all possible slot variations for a gap between appointments
 * Shows every combination of appointment duration and drive time constraints
 * 
 * @param prevAppointment - The appointment before the gap
 * @param nextAppointment - The appointment after the gap
 * @param startingLocation - Coordinates of starting location (home/office or prev appointment)
 * @param endingLocation - Coordinates of ending location (next appointment)
 * @returns Array of all valid slot variations sorted by priority
 */
export const calculateDynamicSlotVariations = (
  prevAppointment: Appointment,
  nextAppointment: Appointment,
  _startingLocation?: { lat: number; lng: number },
  _endingLocation?: { lat: number; lng: number }
): DynamicSlotVariation[] => {
  const variations: DynamicSlotVariation[] = [];
  
  // Calculate gap boundaries
  // IMPORTANT: endDateTime already includes buffer time (e.g., 9:00-10:00 includes 45min service + 15min buffer)
  // Driver leaves at endDateTime (10:00), must arrive at next startDateTime (11:15)
  // So the gap is: 10:00 - 11:15 = 75 minutes total
  
  // Gap starts when driver leaves (buffer already included in endDateTime)
  const gapStartTime = new Date(prevAppointment.endDateTime.getTime());
  
  // Gap ends when driver must arrive at next appointment (no buffer subtraction needed)
  const gapEndTime = new Date(nextAppointment.startDateTime.getTime());
  
  // Total available time in the gap (in minutes)
  const totalGapMinutes = Math.max(
    (gapEndTime.getTime() - gapStartTime.getTime()) / (1000 * 60),
    0
  );
  
  // Minimum drive time requirement: 5 minutes each direction
  const MIN_DRIVE_TIME_EACH = 5;
  const MIN_COMBINED_DRIVE = MIN_DRIVE_TIME_EACH * 2;
  const DRIVE_TIME_INCREMENT = 5; // Generate variations in 5-minute increments

  // For each appointment duration, calculate ALL possible time slot variations
  DYNAMIC_APPOINTMENT_DURATIONS.forEach((appointmentDuration) => {
    // Maximum combined drive time = total gap - appointment duration
    const maxCombinedDriveTime = totalGapMinutes - appointmentDuration;
    
    // Only valid if there's enough time for appointment AND minimum drive times
    const isValid = maxCombinedDriveTime >= MIN_COMBINED_DRIVE;
    
    if (isValid) {
      // Generate all possible time variations based on different drive time scenarios
      const timeVariations: TimeSlotVariation[] = [];
      
      // We want to show ALL possible combinations of drive TO and drive FROM
      // Not just variations that fill the entire gap
      // Start with minimum (5+5) up to maximum available
      
      for (let combinedDrive = MIN_COMBINED_DRIVE; combinedDrive <= maxCombinedDriveTime; combinedDrive += DRIVE_TIME_INCREMENT) {
        // For each combined drive time, generate different splits
        for (let driveTimeTo = MIN_DRIVE_TIME_EACH; driveTimeTo <= combinedDrive - MIN_DRIVE_TIME_EACH; driveTimeTo += DRIVE_TIME_INCREMENT) {
          const driveTimeFrom = combinedDrive - driveTimeTo;
          
          // Skip if drive FROM is less than minimum
          if (driveTimeFrom < MIN_DRIVE_TIME_EACH) continue;
          
          // Calculate start and end times for this variation
          const slotStartTime = new Date(gapStartTime.getTime() + (driveTimeTo * 60 * 1000));
          const slotEndTime = new Date(slotStartTime.getTime() + (appointmentDuration * 60 * 1000));
          
          timeVariations.push({
            driveTimeTo,
            driveTimeFrom,
            slotStartTime,
            slotEndTime
          });
        }
      }
      
      // Calculate utilization (how much of the gap is used by the appointment itself)
      const utilizationPercent = Math.round((appointmentDuration / totalGapMinutes) * 100);
      
      // Determine priority based on utilization and time efficiency
      let priority: 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM';
      
      // High priority: Good utilization (60-85%) - leaves reasonable drive time
      if (utilizationPercent >= 60 && utilizationPercent <= 85) {
        priority = 'HIGH';
      } 
      // Low priority: Very low utilization (<40%) or very high (>90%) - too much wasted time or too tight
      else if (utilizationPercent < 40 || utilizationPercent > 90) {
        priority = 'LOW';
      }
      
      // Special case: If this is a standard duration (60 min), boost priority
      if (appointmentDuration === 60 && priority === 'MEDIUM') {
        priority = 'HIGH';
      }
      
      variations.push({
        appointmentDuration,
        maxCombinedDriveTime,
        totalGapMinutes,
        isValid: true,
        priority,
        utilizationPercent,
        timeVariations
      });
    }
  });
  
  // Sort by priority (HIGH first), then by utilization (better utilization first)
  return variations.sort((a, b) => {
    const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    
    // Within same priority, prefer better utilization (closer to 70%)
    const aUtilDiff = Math.abs(a.utilizationPercent - 70);
    const bUtilDiff = Math.abs(b.utilizationPercent - 70);
    return aUtilDiff - bUtilDiff;
  });
};